// src/components/Navbar.js
import React, { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import LockIcon from "@mui/icons-material/Lock";
import KeyIcon from "@mui/icons-material/VpnKey";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import { useTranslation } from "react-i18next";
import { MdLogout, MdLogin } from "react-icons/md";
import { FaGlobe } from "react-icons/fa";
import { useCrypto } from "../context/CryptoContext";
import ChangePassphraseDialog from "./ChangePassphraseDialog";

const NAVY_GRADIENT =
  "linear-gradient(135deg, rgba(11,31,77,0.92) 0%, rgba(18,58,143,0.92) 55%, rgba(23,105,194,0.92) 100%)";

// Shows: Sign out (user) | Exit guest (guest) | Log in (neither)
const AuthButton = ({ user, guest, onSignOut, onSignIn, fullWidth }) => {
  const { t } = useTranslation();

  let label, onClick, Icon;
  const loggedIn = !!user || !!guest;
  if (user) {
    label = t("signOut");
    onClick = onSignOut;
    Icon = MdLogout;
  } else if (guest) {
    label = t("Exit guest");
    onClick = onSignOut;
    Icon = MdLogout;
  } else {
    label = t("Log in");
    onClick = onSignIn;
    Icon = MdLogin;
  }

  return (
    <Button
      variant={loggedIn ? "outlined" : "contained"}
      onClick={onClick}
      startIcon={<Icon />}
      fullWidth={fullWidth}
      sx={{
        whiteSpace: "nowrap",
        textTransform: "none",
        fontWeight: 700,
        borderRadius: "50px",
        px: 2.5,
        transition: "all .2s ease",
        ...(loggedIn
          ? {
              color: "#fff",
              borderColor: "rgba(255,255,255,0.6)",
              "&:hover": {
                background: "#d32f2f",
                borderColor: "#d32f2f",
              },
            }
          : {
              color: "#0b1f4d",
              background: "#fff",
              boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
              "&:hover": {
                background: "#f3f7ff",
                transform: "translateY(-1px)",
              },
            }),
      }}
    >
      {label}
    </Button>
  );
};

// Change passphrase + Lock buttons. Visible only when unlocked.
const SecurityActions = ({ onChangePass, stacked }) => {
  const { t } = useTranslation();
  const { status, lock } = useCrypto();
  const location = useLocation();

  if (status !== "unlocked") return null;

  // Lock wipes key + unmounts editor → unsaved work lost. Hide while editing.
  const isEditing = ["/edit", "/page2"].includes(location.pathname);

  const btnSx = {
    color: "#fff",
    textTransform: "none",
    whiteSpace: "nowrap",
    borderRadius: "50px",
    px: 1.5,
    justifyContent: stacked ? "flex-start" : "center",
    "&:hover": { background: "rgba(255,255,255,0.15)" },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: stacked ? "column" : "row",
        alignItems: stacked ? "stretch" : "center",
        gap: 0.5,
        width: stacked ? "100%" : "auto",
      }}
    >
      <Button startIcon={<KeyIcon />} onClick={onChangePass} sx={btnSx}>
        {t("Change passphrase")}
      </Button>
      {!isEditing && (
        <Button startIcon={<LockIcon />} onClick={lock} sx={btnSx}>
          {t("Lock")}
        </Button>
      )}
    </Box>
  );
};

const Navbar = ({ user, guest, onSignOut, onSignIn }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [passDialogOpen, setPassDialogOpen] = useState(false);

  const handleChange = (event) => {
    const newLang = event.target.value;
    i18n.changeLanguage(newLang);
    localStorage.setItem("appLanguage", newLang);
  };

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const menuItems = [
    { label: t("home"), href: "/" },
    { label: t("aboutMe"), href: "/about" },
    { label: t("support"), href: "/support" },
  ];

  const isActive = (href) =>
    href === "/"
      ? location.pathname === "/" || location.pathname === "/home"
      : location.pathname === href;

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: NAVY_GRADIENT,
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          borderBottom: "1px solid rgba(255,255,255,0.18)",
          boxShadow: "0 8px 30px rgba(8,40,90,0.25)",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", minHeight: 68 }}>
          {/* Left: hamburger (mobile) / logo / links (PC) */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ display: { xs: "flex", md: "none" }, mr: 1 }}>
              <IconButton
                color="inherit"
                onClick={toggleDrawer(true)}
                aria-label="menu"
                sx={{ "&:hover": { background: "rgba(255,255,255,0.15)" } }}
              >
                <MenuIcon />
              </IconButton>
            </Box>

            <img
              src="/vardion-high-resolution-logo-transparent.png"
              alt={t("Vardion Logo")}
              style={{
                height: 40,
                filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.3))",
              }}
            />

            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 0.5,
                ml: 3,
              }}
            >
              {menuItems.map((item) => {
                const active = isActive(item.href);
                return (
                  <Button
                    key={item.label}
                    component={RouterLink}
                    to={item.href}
                    sx={{
                      color: "#fff",
                      textTransform: "none",
                      fontWeight: active ? 700 : 500,
                      borderRadius: "50px",
                      px: 2,
                      background: active
                        ? "rgba(255,255,255,0.22)"
                        : "transparent",
                      transition: "all .2s ease",
                      "&:hover": { background: "rgba(255,255,255,0.18)" },
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Box>
          </Box>

          {/* Right: language, security, auth */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, md: 1.5 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: "#fff",
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: "50px",
                pl: 1.5,
                pr: 0.5,
              }}
            >
              <FaGlobe style={{ width: 16, height: 16 }} />
              <Select
                value={i18n.language}
                onChange={handleChange}
                variant="standard"
                disableUnderline
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  minWidth: 56,
                  "& .MuiSelect-select": { py: 0.75, pl: 0.5 },
                  "& .MuiSelect-icon": { color: "#fff" },
                }}
              >
                <MenuItem value="en">ENG</MenuItem>
                <MenuItem value="gr">GR</MenuItem>
              </Select>
            </Box>

            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              <SecurityActions onChangePass={() => setPassDialogOpen(true)} />
            </Box>

            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              <AuthButton
                user={user}
                guest={guest}
                onSignOut={onSignOut}
                onSignIn={onSignIn}
              />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer for mobile */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        slotProps={{
          paper: {
            sx: {
              background:
                "linear-gradient(160deg, #0b1f4d 0%, #123a8f 60%, #1769c2 100%)",
              color: "#fff",
              borderTopRightRadius: 24,
              borderBottomRightRadius: 24,
            },
          },
        }}
      >
        <Box
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
          sx={{ width: 270, pt: 2 }}
        >
          <Box sx={{ px: 2, pb: 2 }}>
            <img
              src="/vardion-high-resolution-logo-transparent.png"
              alt={t("Vardion Logo")}
              style={{ height: 44 }}
            />
          </Box>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.2)" }} />

          <List sx={{ px: 1 }}>
            {menuItems.map((item) => (
              <ListItemButton
                key={item.label}
                component={RouterLink}
                to={item.href}
                selected={isActive(item.href)}
                sx={{
                  borderRadius: "14px",
                  mb: 0.5,
                  "&.Mui-selected": { background: "rgba(255,255,255,0.22)" },
                  "&.Mui-selected:hover, &:hover": {
                    background: "rgba(255,255,255,0.18)",
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  slotProps={{ primary: { sx: { fontWeight: 600 } } }}
                />
              </ListItemButton>
            ))}

            <ListItem sx={{ px: 1, pt: 1 }}>
              <SecurityActions
                stacked
                onChangePass={() => setPassDialogOpen(true)}
              />
            </ListItem>

            <ListItem sx={{ px: 1, pt: 1 }}>
              <AuthButton
                fullWidth
                user={user}
                guest={guest}
                onSignOut={onSignOut}
                onSignIn={onSignIn}
              />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      <ChangePassphraseDialog
        open={passDialogOpen}
        onClose={() => setPassDialogOpen(false)}
      />
    </>
  );
};

export default Navbar;
